import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, AUTH_EXPIRED_EVENT } from '../api/client';
import type { Session } from '../types';
import { AuthContext } from './context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session>();

  useEffect(() => {
    api.restoreSession()
      .then(setSession)
      .catch(() => setSession(undefined))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const expire = () => setSession(undefined);
    window.addEventListener(AUTH_EXPIRED_EVENT, expire);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, expire);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password);
    if ('mfaRequired' in result) return result;
    setSession(result);
    return undefined;
  }, []);

  const verifyMfa = useCallback(async (challengeId: string, code: string) => {
    setSession(await api.verifyMfa(challengeId, code));
  }, []);

  const updateProfile = useCallback(async (displayName: string) => {
    setSession(await api.updateProfile(displayName));
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    setSession(await api.changePassword(currentPassword, newPassword));
  }, []);

  const refreshSession = useCallback(async () => {
    setSession(await api.session(true));
  }, []);

  const logout = useCallback(async () => {
    setSession(undefined);
    await api.logout();
  }, []);

  const value = useMemo(
    () => ({ loading, session, login, verifyMfa, updateProfile, changePassword, refreshSession, logout }),
    [loading, session, login, verifyMfa, updateProfile, changePassword, refreshSession, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
